import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/live-support`;
const TOKEN_KEY = "ls_agent_token";

/* ------------------------------ helpers ------------------------------ */
function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 30) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

function initials(name) {
  const n = (name || "U").trim();
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

// stable color from a string (for avatars)
function hueFromStr(s) {
  const str = String(s || "user");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}

/* ------------------------------ root ------------------------------ */
export default function LiveSupportAgentApp() {
  const [agent, setAgent] = useState(null);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const tk = localStorage.getItem(TOKEN_KEY);
      if (!tk) { setBooting(false); return; }
      try {
        const { data } = await axios.get(`${API}/agent/me`, { headers: authHeaders() });
        setAgent(data?.agent || null);
      } catch (_) { localStorage.removeItem(TOKEN_KEY); }
      finally { setBooting(false); }
    })();
  }, []);

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white text-sm"
           style={{ background: "radial-gradient(circle at 20% 20%, #0b1220 0%, #020617 100%)" }}>
        <div className="flex items-center gap-3 opacity-80">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
          Initialising console…
        </div>
      </div>
    );
  }
  if (!agent) {
    return <LoginScreen onLogin={(a) => setAgent(a)} error={error} setError={setError} />;
  }
  return <Dashboard agent={agent} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAgent(null); }} />;
}

/* ------------------------------ login ------------------------------ */
function LoginScreen({ onLogin, error, setError }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || !password || loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/agent/login`, { phone: phone.trim(), password });
      if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
      // Fetch the full agent doc (with permissions) so the dashboard knows what powers we have
      let fullAgent = data?.agent;
      try {
        const me = await axios.get(`${API}/agent/me`, { headers: { Authorization: `Bearer ${data?.token}` } });
        if (me?.data?.agent) fullAgent = me.data.agent;
      } catch (_) { /* fallback to login agent payload */ }
      onLogin(fullAgent);
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
         style={{ background: "#05080f" }}>
      {/* decorative blurs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-40"
           style={{ background: "radial-gradient(circle, #10b981 0%, transparent 60%)", filter: "blur(60px)" }} />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full opacity-30"
           style={{ background: "radial-gradient(circle, #6366f1 0%, transparent 60%)", filter: "blur(60px)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
           style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />

      <div className="w-full max-w-md relative z-10" data-testid="ls-agent-login">
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-4 shadow-2xl shadow-emerald-500/30 ring-1 ring-white/10"
               style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}>
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
              <path d="M21 14a3 3 0 0 1-3 3h-1v-6h1a3 3 0 0 1 3 3z" />
              <path d="M3 14a3 3 0 0 0 3 3h1v-6H6a3 3 0 0 0-3 3z" />
              <path d="M21 16v1a4 4 0 0 1-4 4h-3" />
            </svg>
          </div>
          <h1 className="text-[28px] font-black text-white tracking-tight">Support Console</h1>
          <p className="text-slate-400 text-[13px] mt-1.5 tracking-wide">Maxlive88 · Live agent workspace</p>
        </div>

        <form onSubmit={submit}
              className="rounded-2xl p-7 space-y-5 ring-1 ring-white/10 shadow-2xl shadow-black/40"
              style={{ background: "linear-gradient(180deg, rgba(15,23,42,.85) 0%, rgba(2,6,23,.85) 100%)", backdropFilter: "blur(20px)" }}>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-400 mb-2">Phone Number</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
              </span>
              <input data-testid="ls-login-phone" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus
                     placeholder="01700000000"
                     className="w-full h-12 pl-10 pr-3 rounded-xl bg-slate-900/70 border border-white/10 text-white text-[14px] focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20 placeholder-slate-600 transition-all" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-400 mb-2">Password</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              </span>
              <input data-testid="ls-login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="••••••••"
                     className="w-full h-12 pl-10 pr-12 rounded-xl bg-slate-900/70 border border-white/10 text-white text-[14px] focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20 placeholder-slate-600 transition-all" />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 px-2 text-[10px] font-bold rounded-md text-slate-400 hover:text-white hover:bg-white/5 uppercase tracking-wider">
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="ls-login-error">
              <svg className="shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <span className="leading-snug">{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="ls-login-submit"
                  className="group w-full h-12 rounded-xl font-bold text-[14px] tracking-wide text-white inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 active:scale-[0.99]"
                  style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
            {loading ? (
              <><span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Signing in…</>
            ) : (
              <>Sign In <svg className="transition-transform group-hover:translate-x-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg></>
            )}
          </button>
        </form>

        <p className="text-center text-slate-500 text-[11px] mt-5 tracking-wide">Authorized agents only · End-to-end secured session</p>
      </div>
    </div>
  );
}

/* ------------------------------ dashboard ------------------------------ */
function Dashboard({ agent, onLogout }) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [presence, setPresence] = useState("online"); // online | away
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ls_sound") !== "0");
  const [now, setNow] = useState(Date.now());
  const [imagePreview, setImagePreview] = useState(null); // lightbox URL
  const [quickReplies, setQuickReplies] = useState(QUICK_REPLIES_FALLBACK);
  // Tickets (form submissions) — second pane in the agent console
  const [view, setView] = useState("chats"); // "chats" | "tickets"
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({ pending: 0, in_progress: 0, resolved: 0, rejected: 0 });
  const [ticketStatusFilter, setTicketStatusFilter] = useState("pending");
  const [ticketSearch, setTicketSearch] = useState("");
  const [viewingTicket, setViewingTicket] = useState(null); // ticket id
  // Customer profile sidebar — defaults open on xl screens
  const [profileOpen, setProfileOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1280px)").matches;
  });
  // Close-session modal (Resolution Note prompt)
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const threadRef = useRef(null);
  const lastTsRef = useRef("");
  const prevTotalRef = useRef(0);
  const prevNeedsAgentRef = useRef(-1);
  const prevMsgIdsRef = useRef(new Set());
  const audioRef = useRef(null);
  const typingPingAtRef = useRef(0);
  const [localAgentTypingAt, setLocalAgentTypingAt] = useState(0);

  // tick "now" each 2s for relative timestamps + typing indicator freshness
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(t);
  }, []);

  // Load admin-managed Quick Replies once after login (fallback to bundled set on error)
  useEffect(() => {
    axios.get(`${API}/agent/quick-replies`, { headers: authHeaders() })
      .then((r) => {
        const items = r.data?.items || [];
        if (items.length) {
          setQuickReplies(items.map((q) => ({
            cat: q.category, category: q.category,
            l: q.label,      label: q.label,
            t: q.text,       text: q.text,
            order: q.order,
          })));
        }
      })
      .catch(() => {});
  }, []);

  // chime — minimal beep using Web Audio API (no asset deps)
  const playChime = useCallback((variant = "inbox") => {
    if (!soundOn) return;
    try {
      const ctx = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = ctx;
      // Two-tone bell — "inbox" = single high note, "message" = high then mid (warmer)
      const freqs = variant === "message" ? [988, 740] : [880];
      let t0 = ctx.currentTime;
      freqs.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + 0.32);
        t0 += i === 0 && freqs.length > 1 ? 0.14 : 0;
      });
    } catch (_) { /* ignore */ }
  }, [soundOn]);

  const loadSessions = useCallback(async () => {
    try {
      const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { data } = await axios.get(`${API}/agent/sessions${params}`, { headers: authHeaders() });
      const items = data?.items || [];
      // unread waiting count for chime trigger (skip the very first load to avoid boot-time chime spam)
      const unreadTotal = items.reduce((acc, s) => acc + (s.unread_for_agent || 0), 0);
      if (unreadTotal > prevTotalRef.current && prevTotalRef.current >= 0) playChime("inbox");
      prevTotalRef.current = unreadTotal;
      // Stronger chime + visual pulse when count of "needs-agent" sessions
      // increases — agent should react to these faster than passive FAQ rows.
      const needsAgentTotal = items.filter((s) => s.needs_agent && s.status === "open").length;
      if (needsAgentTotal > prevNeedsAgentRef.current && prevNeedsAgentRef.current >= 0) {
        playChime("inbox");
        playChime("inbox"); // double-tap chime = "urgent"
      }
      prevNeedsAgentRef.current = needsAgentTotal;
      setSessions(items);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      }
    }
  }, [statusFilter, playChime]);

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 5000);
    return () => clearInterval(t);
  }, [loadSessions]);

  // ─── Tickets (form-submission tickets) ───
  const loadTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (ticketStatusFilter && ticketStatusFilter !== "all") params.set("status", ticketStatusFilter);
      if (ticketSearch.trim()) params.set("q", ticketSearch.trim());
      const { data } = await axios.get(`${API}/agent/tickets?${params.toString()}`, { headers: authHeaders() });
      setTickets(data?.items || []);
      setTicketCounts(data?.counts || {});
    } catch (e) { /* ignore */ }
  }, [ticketStatusFilter, ticketSearch]);

  useEffect(() => {
    // Always keep counts fresh (so the header badge updates while user is in chats view)
    loadTickets();
    const t = setInterval(loadTickets, 12000);
    return () => clearInterval(t);
  }, [loadTickets]);

  // Chime when a new pending ticket arrives
  const prevPendingRef = useRef(0);
  useEffect(() => {
    const p = ticketCounts.pending || 0;
    if (prevPendingRef.current && p > prevPendingRef.current) {
      playChime("inbox");
    }
    prevPendingRef.current = p;
  }, [ticketCounts.pending, playChime]);

  const scrollBottom = () => {
    setTimeout(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, 60);
  };

  const openSession = async (sid) => {
    setActiveId(sid);
    setMessages([]);
    lastTsRef.current = "";
    prevMsgIdsRef.current = new Set();
    try {
      const { data } = await axios.get(`${API}/agent/session/${sid}/messages`, { headers: authHeaders() });
      setActive(data?.session || null);
      const items = data?.items || [];
      setMessages(items);
      items.forEach((m) => prevMsgIdsRef.current.add(m.id));
      if (items.length) lastTsRef.current = items[items.length - 1].created_at;
      scrollBottom();
      loadSessions();
    } catch (e) { console.warn(e); }
  };

  // Poll active thread
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(async () => {
      try {
        const since = lastTsRef.current ? `?since_iso=${encodeURIComponent(lastTsRef.current)}` : "";
        const { data } = await axios.get(`${API}/agent/session/${activeId}/messages${since}`, { headers: authHeaders() });
        if (data?.session) setActive(data.session);
        const items = data?.items || [];
        if (items.length) {
          // Chime ONLY when a brand-new user message arrives (not on first load, not on our own messages)
          const newUserMsg = items.some((m) => m.sender_type === "user" && !prevMsgIdsRef.current.has(m.id));
          if (newUserMsg) playChime("message");
          setMessages((prev) => {
            // ── Deduplicate by message id ──
            // Avoids visible duplicates from: (a) optimistic insert on send(),
            // (b) `since_iso` boundary returning the same message twice,
            // (c) overlapping poll intervals on slow networks.
            const seen = new Set(prev.map((m) => m.id));
            const fresh = items.filter((m) => !seen.has(m.id));
            if (fresh.length === 0) {
              lastTsRef.current = items[items.length - 1].created_at;
              return prev;
            }
            fresh.forEach((m) => prevMsgIdsRef.current.add(m.id));
            lastTsRef.current = items[items.length - 1].created_at;
            return [...prev, ...fresh];
          });
          scrollBottom();
        }
      } catch (_) { /* polling errors ignored */ }
    }, 3000);
    return () => clearInterval(t);
  }, [activeId, playChime]);

  const sendReply = async (attachments = []) => {
    if (!activeId || sending) return;
    const text = reply.trim();
    if (!text && (!attachments || attachments.length === 0)) return;
    setSending(true);
    setReply("");
    typingPingAtRef.current = 0; // allow next typing burst
    try {
      const { data } = await axios.post(`${API}/agent/session/${activeId}/message`, { text, attachments }, { headers: authHeaders() });
      if (data?.message) {
        prevMsgIdsRef.current.add(data.message.id);
        setMessages((m) => [...m, data.message]);
        lastTsRef.current = data.message.created_at;
        scrollBottom();
      }
      loadSessions();
    } catch (e) { setReply(text); }
    finally { setSending(false); }
  };

  // Send typing ping at most every 2.5s while composing
  const pingTyping = useCallback(() => {
    if (!activeId) return;
    const t = Date.now();
    setLocalAgentTypingAt(t);
    if (t - typingPingAtRef.current < 2500) return;
    typingPingAtRef.current = t;
    axios.post(`${API}/agent/session/${activeId}/typing`, {}, { headers: authHeaders() }).catch(() => {});
  }, [activeId]);

  // Is the user currently typing? (their last ping < 6s ago)
  // Depend on `now` so this recomputes on tick.
  const userIsTyping = useMemo(() => {
    if (!active?.user_typing_at) return false;
    try { return (now - new Date(active.user_typing_at).getTime()) < 6000; } catch { return false; }
  }, [active, now]);

  // Agent's own (local) typing — recently pinged in last 6s
  const agentIsTypingLocal = useMemo(
    () => localAgentTypingAt > 0 && (now - localAgentTypingAt) < 6000,
    [localAgentTypingAt, now]
  );
  const bothTyping = userIsTyping && agentIsTypingLocal;

  const claim = async () => {
    if (!activeId) return;
    try {
      await axios.post(`${API}/agent/session/${activeId}/claim`, {}, { headers: authHeaders() });
      openSession(activeId);
    } catch (e) { alert(e?.response?.data?.detail || "Claim failed"); }
  };

  const closeSession = () => {
    if (!activeId) return;
    setCloseNote("");
    setShowCloseModal(true);
  };

  const confirmCloseSession = async () => {
    if (!activeId) return;
    const note = closeNote.trim().slice(0, 500);
    try {
      await axios.post(
        `${API}/agent/session/${activeId}/close`,
        note ? { resolution_note: note } : {},
        { headers: authHeaders() },
      );
      setShowCloseModal(false);
      setCloseNote("");
      openSession(activeId);
    } catch (_) { /* ignore */ }
  };

  const counts = useMemo(() => ({
    open: sessions.filter((s) => s.status === "open").length,
    claimed: sessions.filter((s) => s.status === "claimed").length,
    closed: sessions.filter((s) => s.status === "closed").length,
  }), [sessions]);

  const totalUnread = useMemo(
    () => sessions.reduce((acc, s) => acc + (s.unread_for_agent || 0), 0),
    [sessions]
  );

  const filteredSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? sessions : sessions.filter((s) => {
      return (
        (s.username || "").toLowerCase().includes(q) ||
        (s.player_id || "").toLowerCase().includes(q) ||
        (s.user_phone || "").toLowerCase().includes(q) ||
        (s.last_message || "").toLowerCase().includes(q)
      );
    });
    // Sort: needs-agent sessions float to the top of the inbox so urgent
    // requests are never lost behind passive FAQ-browsers. Backend default
    // order (newest activity first) is preserved as the secondary key.
    return [...base].sort((a, b) => {
      const an = a.needs_agent ? 1 : 0;
      const bn = b.needs_agent ? 1 : 0;
      if (an !== bn) return bn - an;
      const at = new Date(a.last_message_at || a.started_at || 0).getTime();
      const bt = new Date(b.last_message_at || b.started_at || 0).getTime();
      return bt - at;
    });
  }, [sessions, search]);

  const toggleSound = () => {
    setSoundOn((v) => {
      const nv = !v;
      localStorage.setItem("ls_sound", nv ? "1" : "0");
      return nv;
    });
  };

  return (
    <div className="min-h-screen flex flex-col text-slate-200" style={{ background: "#080c16" }} data-testid="ls-agent-dashboard">
      {/* Global keyframes for live-typing pulse rings */}
      <style>{`
        @keyframes ls-user-pulse {
          0%   { transform: scale(1);    opacity: 0.55; }
          70%  { transform: scale(1.55); opacity: 0;    }
          100% { transform: scale(1.55); opacity: 0;    }
        }
        @keyframes ls-ping-bi {
          0%   { transform: scale(1);   opacity: 0.85; }
          80%  { transform: scale(2.2); opacity: 0;    }
          100% { transform: scale(2.2); opacity: 0;    }
        }
      `}</style>
      {/* image lightbox */}
      {imagePreview && (
        <div onClick={() => setImagePreview(null)} data-testid="ls-agent-image-lightbox"
             className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out">
          <img src={imagePreview} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" />
          <button onClick={(e) => { e.stopPropagation(); setImagePreview(null); }}
                  className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      )}
      {/* ──────── Close-Session Modal with optional Resolution Note ──────── */}
      {showCloseModal && (
        <div data-testid="ls-close-modal"
             className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setShowCloseModal(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-md rounded-2xl ring-1 ring-white/10 shadow-2xl overflow-hidden"
               style={{ background: "linear-gradient(180deg, #0f1626 0%, #0a1020 100%)" }}>
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-emerald-500/15 ring-1 ring-emerald-400/30 text-emerald-300">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-black text-white tracking-tight">End this chat session?</div>
                <div className="text-[11px] text-slate-400 mt-0.5">User will see a friendly resolution message + rating prompt.</div>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-1.5">
                  📝 Resolution Note <span className="text-slate-500 font-normal normal-case">(optional, shown to user)</span>
                </label>
                <textarea
                  data-testid="ls-close-note"
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value.slice(0, 500))}
                  rows={4}
                  autoFocus
                  placeholder="যেমন: Deposit credited ৳1,000, TRX verified. Bonus claim approved। কোনো extra issue থাকলে আবার আমাদের জানান।"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus:border-emerald-400/60 focus:outline-none text-[13px] text-slate-100 placeholder:text-slate-600 resize-none transition-colors"
                />
                <div className="flex justify-between items-center mt-1">
                  <div className="text-[10px] text-slate-500">Brief summary helps user feel confident the issue is resolved.</div>
                  <div className="text-[10px] tabular-nums text-slate-500">{closeNote.length}/500</div>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <div className="flex flex-wrap gap-1.5">
                  {[
                    "✅ Deposit credited.",
                    "✅ Withdraw processed.",
                    "✅ Bonus approved.",
                    "✅ Password reset done.",
                    "ℹ️ Provided guidance.",
                  ].map((t) => (
                    <button key={t} type="button"
                            onClick={() => setCloseNote((v) => (v ? `${v} ${t}` : t).slice(0, 500))}
                            className="text-[10.5px] font-semibold px-2 py-1 rounded-md bg-white/[0.04] hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-400/40 text-slate-300 hover:text-emerald-200 transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3.5 border-t border-white/[0.06] flex items-center justify-end gap-2 bg-black/20">
              <button data-testid="ls-close-cancel" onClick={() => setShowCloseModal(false)}
                      className="px-3.5 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-300 text-[12.5px] font-bold transition-colors">
                Cancel
              </button>
              <button data-testid="ls-close-confirm" onClick={confirmCloseSession}
                      className="px-4 h-9 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-white text-[12.5px] font-black tracking-tight shadow-md shadow-emerald-900/40 transition-colors inline-flex items-center gap-1.5">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                End Chat
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ──────────── Header / Top Bar ──────────── */}
      <header className="px-3 sm:px-5 py-3 sm:py-3.5 flex items-center justify-between sticky top-0 z-20 shrink-0 border-b border-white/[0.06]"
              style={{ background: "linear-gradient(180deg, rgba(15,22,38,0.95) 0%, rgba(10,15,28,0.95) 100%)", backdropFilter: "blur(18px)" }}>
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="relative shrink-0">
            <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center text-white font-black text-[13px] tracking-tight shadow-lg shadow-emerald-500/30 ring-1 ring-white/10"
                 style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}>M88</div>
            <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[#080c16] ${presence === "online" ? "bg-emerald-400" : "bg-amber-400"}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-black text-white text-[15px] sm:text-[16px] leading-tight tracking-tight truncate">Support Console</div>
              <span className="hidden sm:inline px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">LIVE</span>
            </div>
            <div className="text-[11px] text-slate-400 truncate flex items-center gap-1.5 mt-0.5">
              <span className="font-semibold text-slate-300">{agent?.name}</span>
              <span className="text-slate-600">·</span>
              <span>{agent?.phone}</span>
            </div>
          </div>
        </div>

        {/* Live KPIs */}
        <div className="hidden md:flex items-center gap-2.5">
          <KpiPill label="Waiting" value={counts.open} accent="#f59e0b" pulse={counts.open > 0} />
          <KpiPill label="Active" value={counts.claimed} accent="#10b981" />
          <KpiPill label="Unread" value={totalUnread} accent="#ef4444" pulse={totalUnread > 0} />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Presence dropdown */}
          <button onClick={() => setPresence((p) => (p === "online" ? "away" : "online"))} data-testid="ls-presence-toggle"
                  title="Toggle presence"
                  className={`h-9 px-2.5 sm:px-3 rounded-lg text-[11px] sm:text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${
                    presence === "online"
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/15"
                      : "bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/15"
                  }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${presence === "online" ? "bg-emerald-400" : "bg-amber-400"}`} />
            <span className="hidden sm:inline capitalize">{presence}</span>
          </button>

          {/* Sound toggle */}
          <button onClick={toggleSound} data-testid="ls-sound-toggle"
                  title={soundOn ? "Notification sound on" : "Notification sound off"}
                  className={`h-9 w-9 rounded-lg border inline-flex items-center justify-center transition-colors ${
                    soundOn
                      ? "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.07]"
                      : "bg-white/[0.02] border-white/5 text-slate-500 hover:text-slate-400"
                  }`}>
            {soundOn ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
            )}
          </button>

          <button onClick={onLogout} data-testid="ls-logout"
                  className="h-9 px-2.5 sm:px-3 rounded-lg text-[11px] sm:text-xs font-bold text-slate-300 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 border border-white/10 inline-flex items-center gap-1.5 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* ──────────── View tabs: Chats / Tickets ──────────── */}
      <div className="px-3 sm:px-5 pt-3 flex items-center gap-2 shrink-0">
        <ViewTab active={view === "chats"} onClick={() => setView("chats")} data-testid="ls-view-chats"
                 label="Live Chats" badge={counts.open || 0} badgeColor="#f59e0b" pulse={(counts.open || 0) > 0}
                 icon={
                   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                 } />
        <ViewTab active={view === "tickets"} onClick={() => setView("tickets")} data-testid="ls-view-tickets"
                 label="Tickets" badge={ticketCounts.pending || 0} badgeColor="#ef4444" pulse={(ticketCounts.pending || 0) > 0}
                 icon={
                   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
                 } />
        <div className="ml-auto md:hidden text-[10px] text-slate-500 font-semibold">
          {totalUnread > 0 && view === "chats" && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">{totalUnread} unread</span>}
        </div>
      </div>

      {view === "tickets" ? (
        <TicketsView
          tickets={tickets}
          counts={ticketCounts}
          statusFilter={ticketStatusFilter}
          setStatusFilter={setTicketStatusFilter}
          search={ticketSearch}
          setSearch={setTicketSearch}
          viewingTicket={viewingTicket}
          setViewingTicket={setViewingTicket}
          onUpdated={loadTickets}
          onOpenSession={(sid) => { setView("chats"); openSession(sid); }}
        />
      ) : (
        <>
      {/* ──────────── Filter chips ──────────── */}
      <div className="px-3 sm:px-5 pt-3 flex flex-wrap items-center gap-1.5 sm:gap-2 shrink-0">
        {[
          { k: "open", l: "Waiting", c: counts.open, color: "#f59e0b" },
          { k: "claimed", l: "Active", c: counts.claimed, color: "#10b981" },
          { k: "closed", l: "Closed", c: counts.closed, color: "#64748b" },
          { k: "all", l: "All", c: sessions.length, color: "#94a3b8" },
        ].map((f) => {
          const active = statusFilter === f.k;
          return (
            <button key={f.k} onClick={() => setStatusFilter(f.k)} data-testid={`ls-filter-${f.k}`}
                    className={`h-8 px-3 rounded-full text-[11px] sm:text-xs font-bold inline-flex items-center gap-2 border transition-all ${
                      active
                        ? "bg-white text-slate-900 border-white shadow-lg"
                        : "bg-white/[0.04] text-slate-300 border-white/10 hover:bg-white/[0.08] hover:border-white/20"
                    }`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: f.color }} />
              {f.l}
              <span className={`px-1.5 h-4 rounded-full text-[10px] font-bold inline-flex items-center ${active ? "bg-slate-900 text-white" : "bg-white/[0.08] text-slate-400"}`}>{f.c}</span>
            </button>
          );
        })}
        <div className="ml-auto md:hidden text-[10px] text-slate-500 font-semibold">
          {totalUnread > 0 && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">{totalUnread} unread</span>}
        </div>
      </div>

      {/* ──────────── Main split ──────────── */}
      <div className="lg:grid lg:grid-cols-[360px_1fr] lg:gap-3 p-3 sm:p-4 h-[calc(100dvh-138px)] lg:h-[calc(100vh-138px)]">
        {/* Session list */}
        <div className={`rounded-2xl overflow-hidden flex-col h-full ring-1 ring-white/[0.06] shadow-2xl shadow-black/30 ${activeId ? "hidden lg:flex" : "flex"}`}
             style={{ background: "linear-gradient(180deg, #0e1525 0%, #0a1020 100%)" }}>
          {/* Search */}
          <div className="px-3 pt-3 pb-2 border-b border-white/[0.05]">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search by user, ID, phone…" data-testid="ls-session-search"
                     className="w-full h-9 pl-9 pr-9 rounded-lg bg-slate-900/70 border border-white/[0.08] text-[12.5px] text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/20" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-500 hover:text-white hover:bg-white/5 flex items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              )}
            </div>
          </div>

          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 flex items-center justify-between border-b border-white/[0.04]">
            <span>Inbox</span>
            <span>{filteredSessions.length} of {sessions.length}</span>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="ls-session-list">
            {filteredSessions.length === 0 ? (
              <div className="p-10 text-center text-[13px] text-slate-500 flex flex-col items-center gap-2">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="opacity-40"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                <div className="font-semibold">{search ? "No matches" : "No active sessions"}</div>
                <div className="text-[11px] text-slate-600">{search ? "Try a different keyword" : "New chats will appear here"}</div>
              </div>
            ) : (
              <div className="py-1">
                {filteredSessions.map((s) => (
                  <SessionRow key={s.id} s={s} active={activeId === s.id} onClick={() => openSession(s.id)} now={now} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Thread panel */}
        <div className={`rounded-2xl flex-col min-h-0 h-full ring-1 ring-white/[0.06] shadow-2xl shadow-black/30 overflow-hidden ${activeId ? "flex" : "hidden lg:flex"}`}
             style={{ background: "linear-gradient(180deg, #0e1525 0%, #0a1020 100%)" }}>
          {!active ? (
            <EmptyThread />
          ) : (
            <>
              <ThreadHeader
                active={active}
                agent={agent}
                userIsTyping={userIsTyping}
                bothTyping={bothTyping}
                onBack={() => { setActiveId(null); setActive(null); setMessages([]); }}
                onClaim={claim}
                onClose={closeSession}
                onToggleProfile={() => setProfileOpen((v) => !v)}
                profileOpen={profileOpen}
              />

              <div ref={threadRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-5 space-y-3" data-testid="ls-thread"
                   style={{ background: "radial-gradient(ellipse at top, rgba(16,185,129,0.04) 0%, transparent 60%), #0a0f1c" }}>
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[12px] text-slate-600">No messages yet in this session</div>
                ) : (
                  messages.map((m, i) => {
                    const prev = messages[i - 1];
                    const showHeader = !prev || prev.sender_type !== m.sender_type || (new Date(m.created_at) - new Date(prev.created_at)) > 5 * 60 * 1000;
                    return <AgentBubble key={m.id} m={m} showHeader={showHeader} onImageOpen={setImagePreview} />;
                  })
                )}
                {userIsTyping && active.status !== "closed" && (
                  <TypingBubble name={active.username || "User"} />
                )}
              </div>

              <Composer
                disabled={active.status === "closed"}
                value={reply}
                onChange={(v) => { setReply(v); if (v) pingTyping(); }}
                onSend={sendReply}
                sending={sending}
                statusClosed={active.status === "closed"}
                sessionId={activeId}
                onImageOpen={setImagePreview}
                quickReplies={quickReplies}
                messages={messages}
              />
            </>
          )}
        </div>
      </div>

      {/* Customer Profile Sidebar (slide-over panel) */}
      <CustomerProfileSidebar
        sessionId={activeId}
        open={!!(activeId && profileOpen)}
        onClose={() => setProfileOpen(false)}
        agent={agent}
      />
        </>
      )}
    </div>
  );
}

/* ------------------------------ subcomponents ------------------------------ */

/* ============================================================
   Customer Profile Sidebar — read-only player context for agent
   (slide-over panel from the right). Manual refresh button only.
   ============================================================ */
function CustomerProfileSidebar({ sessionId, open, onClose, agent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview"); // overview | site_dep | site_wd | agent_dep | agent_wd | turnover | kyc | player
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState(null); // {kind, text}

  const perms = agent?.permissions || [];
  const has = useCallback((p) => perms.includes("*") || perms.includes(p), [perms]);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/agent/session/${sessionId}/customer-profile`, { headers: authHeaders() });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "লোড করতে সমস্যা হয়েছে");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) load();
    if (!open || !sessionId) { setData(null); setError(""); }
  }, [open, sessionId, load]);

  // ── action runner ──────────────────────────────────────────────
  const runAction = useCallback(async (path, body, { method = "POST", successMsg = "Done" } = {}) => {
    if (!sessionId) return null;
    setActionBusy(true); setToast(null);
    try {
      const opts = { headers: authHeaders() };
      const url = `${API}/agent/session/${sessionId}${path}`;
      const res = method === "GET" ? await axios.get(url, opts) : await axios.post(url, body || {}, opts);
      setToast({ kind: "ok", text: successMsg });
      await load();
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Action failed";
      setToast({ kind: "err", text: msg });
      return null;
    } finally {
      setActionBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }, [sessionId, load]);

  return (
    <>
      {/* mobile/tablet backdrop */}
      {open && (
        <div onClick={onClose}
             className="xl:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm transition-opacity"
             data-testid="ls-profile-backdrop" />
      )}
      <aside data-testid="ls-customer-profile"
             className={`fixed right-0 top-0 bottom-0 z-40 w-full sm:w-[440px] border-l border-white/[0.06] shadow-2xl shadow-black/60 transition-transform duration-300 flex flex-col ${
               open ? "translate-x-0" : "translate-x-full"
             }`}
             style={{ background: "linear-gradient(180deg, #0b1120 0%, #070b16 100%)" }}>
        <ProfileHeader player={data?.player} loading={loading} onRefresh={load} onClose={onClose} />

        <ProfileTabs tab={tab} setTab={setTab} data={data} has={has} />

        {toast && (
          <div data-testid="ls-profile-toast"
               className={`mx-4 mt-3 px-3 py-2 rounded-lg text-[11.5px] font-semibold border ${
                 toast.kind === "ok"
                   ? "bg-emerald-500/12 border-emerald-400/30 text-emerald-200"
                   : "bg-rose-500/12 border-rose-400/30 text-rose-200"
               }`}>
            {toast.text}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ls-profile-body">
          {error && (
            <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="ls-profile-error">
              {error}
            </div>
          )}
          {loading && !data && (
            <div className="text-[12px] text-slate-500 text-center py-10">Loading…</div>
          )}
          {!loading && !data && !error && (
            <div className="text-[12px] text-slate-500 text-center py-10">কোনো ডেটা নেই</div>
          )}

          {data && tab === "overview" && <OverviewPane data={data} />}
          {data && tab === "player" && <PlayerMgmtPane data={data} has={has} runAction={runAction} actionBusy={actionBusy} />}
          {data && tab === "site_dep" && <TxList title="Site Deposits" bucket={data.site_deposits} kind="deposit" canDecide={has("tx.deposit.decide")} onDecide={(id, action, note) => runAction(`/site-deposits/${id}/decision`, { action, note }, { successMsg: `Deposit ${action}d` })} actionBusy={actionBusy} />}
          {data && tab === "site_wd" && <TxList title="Site Withdrawals" bucket={data.site_withdrawals} kind="withdrawal" canDecide={has("tx.withdrawal.decide")} onDecide={(id, action, note) => runAction(`/site-withdrawals/${id}/decision`, { action, note }, { successMsg: `Withdrawal ${action}d` })} actionBusy={actionBusy} />}
          {data && tab === "agent_dep" && <TxList title="Agent Deposits" bucket={data.agent_deposits} kind="deposit" />}
          {data && tab === "agent_wd" && <TxList title="Agent Withdrawals" bucket={data.agent_withdrawals} kind="withdrawal" canDecide={has("tx.agent_withdrawal.decide")} onDecide={(id, action, note) => runAction(`/agent-withdrawals/${id}/decision`, { action, note }, { successMsg: `Agent withdrawal ${action}d` })} actionBusy={actionBusy} />}
          {data && tab === "turnover" && <TurnoverPane t={data.turnover} />}
          {data && tab === "kyc" && <KycPane k={data.kyc} canDecide={has("kyc.decide")} onDecide={(subId, action, note) => runAction(`/kyc/${subId}/decision`, { action, note }, { successMsg: `KYC ${action}d` })} actionBusy={actionBusy} />}
        </div>

        <div className="px-4 py-2.5 border-t border-white/[0.06] flex items-center justify-between text-[10.5px] text-slate-500 shrink-0">
          <span data-testid="ls-profile-fetched-at">Last refresh: {data?.fetched_at ? fmtTime(data.fetched_at) : "—"}</span>
          <span className={`font-semibold ${perms.length ? "text-emerald-400/80" : "text-amber-400/80"}`}>
            {perms.length ? `${perms.length} permission${perms.length > 1 ? "s" : ""}` : "READ-ONLY"}
          </span>
        </div>
      </aside>
    </>
  );
}

function ProfileHeader({ player, loading, onRefresh, onClose }) {
  const hue = hueFromStr(player?.username || player?.player_id || "u");
  const balance = Number(player?.balance || 0);
  return (
    <div className="px-4 py-4 border-b border-white/[0.06] shrink-0"
         style={{ background: "linear-gradient(180deg, rgba(139,92,246,0.1) 0%, transparent 100%)" }}>
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-black text-[14px] ring-1 ring-white/15 shrink-0"
             style={{ background: `linear-gradient(135deg, hsl(${hue},70%,52%), hsl(${(hue + 40) % 360},70%,40%))` }}>
          {initials(player?.username || player?.player_id || "—")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-white truncate text-[14.5px] leading-tight">
            @{player?.username || "—"}
          </div>
          <div className="text-[11px] text-violet-300 font-mono font-semibold mt-0.5">
            ID #{player?.player_id || "—"}
          </div>
          {player?.full_name && (
            <div className="text-[11px] text-slate-400 mt-0.5 truncate">{player.full_name}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onRefresh} disabled={loading}
                  data-testid="ls-profile-refresh"
                  title="Manual refresh"
                  className="h-8 w-8 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/10 text-slate-300 hover:text-white inline-flex items-center justify-center transition-colors disabled:opacity-50">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                 className={loading ? "animate-spin" : ""}>
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          <button onClick={onClose} data-testid="ls-profile-close"
                  className="h-8 w-8 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-red-500/15 hover:border-red-500/30 text-slate-300 hover:text-red-200 inline-flex items-center justify-center transition-colors">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Balance + contacts strip */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider text-emerald-400/80 font-bold">Balance</div>
          <div className="text-[16px] font-black text-emerald-300 mt-0.5" data-testid="ls-profile-balance">
            ৳ {balance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-bold">Status</div>
          <div className="text-[13px] font-bold text-white mt-0.5 flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${player?.account_blocked ? "bg-red-400" : "bg-emerald-400"}`} />
            {player?.account_blocked ? "Blocked" : (player?.role === "user" ? "Active" : (player?.role || "—"))}
          </div>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <ContactChip label="Phone" value={player?.phone ? `${player?.country_code || ""}${player.phone}` : "—"} verified={player?.phone_verified} />
        <ContactChip label="KYC" value={(player?.kyc_status || "unverified")} highlight={(player?.kyc_status || "").toLowerCase() === "verified"} />
      </div>
    </div>
  );
}

function ContactChip({ label, value, verified, highlight }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`text-[11.5px] font-semibold truncate mt-0.5 flex items-center gap-1 ${highlight ? "text-emerald-300" : "text-slate-200"}`}>
        {value}
        {verified && <span className="text-emerald-400">✓</span>}
      </div>
    </div>
  );
}

function ProfileTabs({ tab, setTab, data, has }) {
  const canPlayer = has?.("player.manage");
  const tabs = [
    { k: "overview", l: "Overview" },
    canPlayer && { k: "player", l: "Player Mgmt" },
    { k: "site_dep", l: "Site Dep", badge: data?.site_deposits?.pending_count },
    { k: "site_wd", l: "Site WD", badge: data?.site_withdrawals?.pending_count },
    { k: "agent_dep", l: "Agent Dep", badge: data?.agent_deposits?.pending_count },
    { k: "agent_wd", l: "Agent WD", badge: data?.agent_withdrawals?.pending_count },
    { k: "turnover", l: "Turnover" },
    { k: "kyc", l: "KYC", badge: data?.kyc?.status === "pending" ? 1 : 0 },
  ].filter(Boolean);
  return (
    <div className="px-3 py-2 border-b border-white/[0.06] shrink-0 overflow-x-auto scrollbar-thin">
      <div className="inline-flex items-center gap-1.5 min-w-max">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ls-profile-tab-${t.k}`}
                  className={`h-8 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5 border transition-colors whitespace-nowrap ${
                    tab === t.k
                      ? "bg-violet-500/15 border-violet-400/40 text-violet-200"
                      : "bg-white/[0.03] border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/[0.06]"
                  }`}>
            {t.l}
            {t.badge ? (
              <span className="h-4 px-1.5 rounded-full text-[9px] font-black bg-amber-500/20 text-amber-300 inline-flex items-center">
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function OverviewPane({ data }) {
  const { player, site_deposits: sd, site_withdrawals: sw, agent_deposits: ad, agent_withdrawals: aw, turnover: t, kyc } = data;
  const bdt = (n) => `৳ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-3" data-testid="ls-profile-overview">
      <StatGrid title="Site Deposits" bucket={sd} positive />
      <StatGrid title="Site Withdrawals" bucket={sw} />
      <StatGrid title="Agent Deposits" bucket={ad} positive />
      <StatGrid title="Agent Withdrawals" bucket={aw} />
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Turnover</div>
        {t?.available ? (
          <>
            <ProgressBar label="Deposit bucket" required={t?.deposit?.required} progress={t?.deposit?.progress} />
            <div className="h-2" />
            <ProgressBar label="Bonus bucket" required={t?.bonus?.required} progress={t?.bonus?.progress} locked={t?.bonus?.locked} />
            <div className="text-[10.5px] mt-2 text-slate-400">
              Withdrawable: <b className={t?.completed ? "text-emerald-300" : "text-amber-300"}>{t?.completed ? "Yes" : "No (turnover pending)"}</b>
            </div>
          </>
        ) : <div className="text-[12px] text-slate-500">Turnover system unavailable</div>}
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">KYC Review</div>
        <div className="flex items-center justify-between">
          <KycBadge status={kyc?.status} />
          <span className="text-[11px] text-slate-400">{kyc?.submissions_count || 0} submission{(kyc?.submissions_count || 0) === 1 ? "" : "s"}</span>
        </div>
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Account</div>
        <Row k="Email" v={player?.email || "—"} />
        <Row k="First deposit method" v={player?.first_deposit_method || "—"} />
        <Row k="Refer code" v={player?.refer_code || "—"} />
        <Row k="VIP tier" v={player?.vip_tier || "Normal"} />
        <Row k="Registered" v={player?.created_at ? fmtTime(player.created_at) : "—"} />
        <Row k="Last login" v={player?.last_login_at ? fmtTime(player.last_login_at) : "—"} />
      </div>
    </div>
  );
}

function StatGrid({ title, bucket, positive }) {
  const bdt = (n) => `৳ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3" data-testid={`ls-stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{title}</div>
        <div className="text-[10px] text-slate-500">{bucket?.total_count || 0} total</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9.5px] text-slate-500 uppercase tracking-wider font-bold">All</div>
          <div className={`text-[14px] font-black ${positive ? "text-emerald-300" : "text-rose-300"}`}>{bdt(bucket?.total_amount)}</div>
        </div>
        <div>
          <div className="text-[9.5px] text-amber-400/80 uppercase tracking-wider font-bold">Pending</div>
          <div className="text-[14px] font-black text-amber-300 flex items-baseline gap-1.5">
            {bdt(bucket?.pending_amount)}
            {bucket?.pending_count > 0 && (
              <span className="text-[9.5px] font-semibold text-amber-400">({bucket.pending_count})</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TxList({ title, bucket, kind, canDecide, onDecide, actionBusy }) {
  const bdt = (n) => `৳ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const rows = bucket?.recent || [];
  const [actingRow, setActingRow] = useState(null); // { id, action }
  const [note, setNote] = useState("");

  const confirm = async () => {
    if (!actingRow) return;
    await onDecide?.(actingRow.id, actingRow.action, note);
    setActingRow(null); setNote("");
  };

  return (
    <div className="space-y-3" data-testid={`ls-txlist-${kind}`}>
      <StatGrid title={title} bucket={bucket} positive={kind === "deposit"} />
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-white/[0.04]">
          Recent {kind === "deposit" ? "deposits" : "withdrawals"} (latest 10)
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-center text-[12px] text-slate-500">No records</div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {rows.map((r) => {
              const isPending = ["pending", "processing", "initiated", "submitted", "review"].includes((r.status || "").toLowerCase());
              return (
                <div key={r.id} className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-bold text-white truncate">{bdt(r.amount)}</div>
                      <div className="text-[10.5px] text-slate-500 truncate">
                        {(r.method || r.label || "—").toString().toUpperCase()}
                        {r.txn_id && <> · <span className="font-mono">{r.txn_id}</span></>}
                      </div>
                      <div className="text-[10px] text-slate-600 truncate">{r.created_at ? fmtTime(r.created_at) : ""}</div>
                    </div>
                    <TxStatusPill status={r.status} />
                  </div>
                  {canDecide && isPending && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <button onClick={() => setActingRow({ id: r.id, action: "approve" })}
                              disabled={actionBusy}
                              data-testid={`ls-tx-approve-${r.id}`}
                              className="h-7 px-3 rounded-md text-[10.5px] font-bold text-emerald-200 bg-emerald-500/12 border border-emerald-400/30 hover:bg-emerald-500/20 inline-flex items-center gap-1 disabled:opacity-50">
                        ✓ Approve
                      </button>
                      <button onClick={() => setActingRow({ id: r.id, action: "reject" })}
                              disabled={actionBusy}
                              data-testid={`ls-tx-reject-${r.id}`}
                              className="h-7 px-3 rounded-md text-[10.5px] font-bold text-rose-200 bg-rose-500/12 border border-rose-400/30 hover:bg-rose-500/20 inline-flex items-center gap-1 disabled:opacity-50">
                        ✕ Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {actingRow && (
        <ConfirmDialog
          title={`${actingRow.action === "approve" ? "Approve" : "Reject"} ${kind}?`}
          subtitle={`This will be recorded in the audit log as you (Support).`}
          confirmKind={actingRow.action === "approve" ? "ok" : "danger"}
          confirmLabel={actingRow.action === "approve" ? "Approve" : "Reject"}
          onCancel={() => { setActingRow(null); setNote(""); }}
          onConfirm={confirm}
          busy={actionBusy}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    placeholder="Optional note (saved to audit log + shown in admin panel)…"
                    data-testid="ls-tx-decision-note"
                    className="w-full rounded-lg bg-slate-900/70 border border-white/[0.08] text-[12.5px] text-white placeholder-slate-500 p-2.5 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/20 resize-none" />
        </ConfirmDialog>
      )}
    </div>
  );
}

function ConfirmDialog({ title, subtitle, confirmKind = "ok", confirmLabel = "Confirm", onCancel, onConfirm, busy, children }) {
  const confirmCls = confirmKind === "danger"
    ? "bg-rose-500/20 hover:bg-rose-500/30 text-rose-100 border-rose-400/40"
    : "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border-emerald-400/40";
  return (
    <div onClick={onCancel} data-testid="ls-confirm-dialog"
         className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-[360px] rounded-2xl border border-white/[0.08] p-5 shadow-2xl"
           style={{ background: "linear-gradient(180deg, #0e1525 0%, #0a1020 100%)" }}>
        <div className="text-[15px] font-black text-white">{title}</div>
        {subtitle && <div className="text-[11.5px] text-slate-400 mt-1">{subtitle}</div>}
        <div className="mt-3">{children}</div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy}
                  className="h-9 px-4 rounded-lg text-[12px] font-bold text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
                  data-testid="ls-confirm-yes"
                  className={`h-9 px-4 rounded-lg text-[12px] font-bold border inline-flex items-center gap-1.5 disabled:opacity-50 ${confirmCls}`}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayerMgmtPane({ data, has, runAction, actionBusy }) {
  const p = data.player || {};
  const blocked = !!p.account_blocked || p.role === "blocked";

  // Local state for forms
  const [blockNote, setBlockNote] = useState("");
  const [pwMode, setPwMode] = useState("temp");
  const [pwReason, setPwReason] = useState("");
  const [tempPw, setTempPw] = useState("");
  const [bets, setBets] = useState(null);
  const [betsLoading, setBetsLoading] = useState(false);

  const loadBets = useCallback(async () => {
    if (!data) return;
    setBetsLoading(true);
    const res = await runAction(`/player/bets?limit=20`, null, { method: "GET", successMsg: "Bets loaded" });
    if (res) setBets(res);
    setBetsLoading(false);
  }, [data, runAction]);

  const doBlock = async (toBlock) => {
    await runAction(`/player/block`, { blocked: toBlock, reason: blockNote }, {
      successMsg: toBlock ? "Account blocked" : "Account unblocked",
    });
  };

  const doWdPerm = async (key, value) => {
    await runAction(`/player/withdraw-perm`, { [key]: value }, { successMsg: "Withdraw permission updated" });
  };

  const doPwReset = async () => {
    setTempPw("");
    const res = await runAction(`/player/password-reset`, { mode: pwMode, reason: pwReason }, {
      successMsg: pwMode === "temp" ? "Temp password generated" : "Force-reset triggered",
    });
    if (res?.temp_password) setTempPw(res.temp_password);
  };

  const do2faReset = async () => {
    await runAction(`/player/2fa-reset`, {}, { successMsg: "2FA cleared — user will re-enroll" });
  };

  return (
    <div className="space-y-3" data-testid="ls-profile-player">
      {/* Block toggle */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Account Status</div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[13px] font-bold flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${blocked ? "bg-rose-400" : "bg-emerald-400"}`} />
            {blocked ? "Blocked" : "Active"}
          </div>
        </div>
        <input value={blockNote} onChange={(e) => setBlockNote(e.target.value)} maxLength={200}
               placeholder="Reason (optional, audit-log)"
               data-testid="ls-player-block-reason"
               className="w-full h-8 px-2.5 rounded-md bg-slate-900/70 border border-white/[0.08] text-[11.5px] text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60" />
        <div className="mt-2 flex items-center gap-1.5">
          {!blocked ? (
            <button onClick={() => doBlock(true)} disabled={actionBusy}
                    data-testid="ls-player-block-btn"
                    className="h-8 px-3 rounded-md text-[11px] font-bold text-rose-200 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 disabled:opacity-50">
              Block Account
            </button>
          ) : (
            <button onClick={() => doBlock(false)} disabled={actionBusy}
                    data-testid="ls-player-unblock-btn"
                    className="h-8 px-3 rounded-md text-[11px] font-bold text-emerald-200 bg-emerald-500/15 border border-emerald-400/30 hover:bg-emerald-500/25 disabled:opacity-50">
              Unblock Account
            </button>
          )}
        </div>
      </div>

      {/* Withdraw permission */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Withdraw Permission</div>
        <PermToggleRow label="Withdraw enabled (account-level)"
                       value={!!p.withdraw_enabled || p.withdraw_enabled === undefined}
                       onChange={(v) => doWdPerm("withdraw_enabled", v)}
                       testid="ls-perm-withdraw-enabled" disabled={actionBusy} />
        <PermToggleRow label="Allow any withdraw method"
                       value={!!p.allow_any_withdraw_method}
                       onChange={(v) => doWdPerm("allow_any_withdraw_method", v)}
                       testid="ls-perm-any-method" disabled={actionBusy} />
        <PermToggleRow label="Allow non-agent withdrawal"
                       value={!!p.allow_non_agent_withdraw}
                       onChange={(v) => doWdPerm("allow_non_agent_withdraw", v)}
                       testid="ls-perm-non-agent" disabled={actionBusy} />
      </div>

      {/* Password reset */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Password Reset</div>
        <div className="flex items-center gap-1.5 mb-2">
          <ModeChip label="Set temp password" active={pwMode === "temp"} onClick={() => setPwMode("temp")} testid="ls-pwmode-temp" />
          <ModeChip label="Force reset (next login)" active={pwMode === "force"} onClick={() => setPwMode("force")} testid="ls-pwmode-force" />
        </div>
        <input value={pwReason} onChange={(e) => setPwReason(e.target.value)} maxLength={200}
               placeholder="Reason (optional)"
               data-testid="ls-pwreset-reason"
               className="w-full h-8 px-2.5 rounded-md bg-slate-900/70 border border-white/[0.08] text-[11.5px] text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60 mb-2" />
        <button onClick={doPwReset} disabled={actionBusy}
                data-testid="ls-pwreset-btn"
                className="h-8 px-3 rounded-md text-[11px] font-bold text-violet-200 bg-violet-500/15 border border-violet-400/30 hover:bg-violet-500/25 disabled:opacity-50">
          {pwMode === "temp" ? "Generate Temp Password" : "Trigger Force Reset"}
        </button>
        {tempPw && (
          <div className="mt-2 p-2.5 rounded-md bg-emerald-500/12 border border-emerald-400/30 text-emerald-200 font-mono text-[12px]" data-testid="ls-temp-password">
            🔐 Temp Password: <b>{tempPw}</b>
            <div className="text-[10px] text-emerald-300/80 mt-1">Share with user via secure channel. They MUST reset on first login.</div>
          </div>
        )}
      </div>

      {/* 2FA */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Two-Factor Authentication</div>
        <div className="text-[11.5px] text-slate-400 mb-2">
          Wipes the player&apos;s 2FA secret. They will be prompted to re-enroll on next login.
        </div>
        <button onClick={do2faReset} disabled={actionBusy}
                data-testid="ls-2fa-reset-btn"
                className="h-8 px-3 rounded-md text-[11px] font-bold text-amber-200 bg-amber-500/15 border border-amber-400/30 hover:bg-amber-500/25 disabled:opacity-50">
          Reset 2FA
        </button>
      </div>

      {/* Bet history */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Bet History</div>
          <button onClick={loadBets} disabled={actionBusy || betsLoading}
                  data-testid="ls-bets-load"
                  className="h-7 px-2.5 rounded-md text-[10.5px] font-bold text-slate-300 bg-white/[0.04] border border-white/10 hover:bg-white/10 disabled:opacity-50">
            {betsLoading ? "Loading…" : (bets ? "Refresh" : "Load")}
          </button>
        </div>
        {bets ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="text-[11.5px]"><span className="text-slate-500">Total bets:</span> <b className="text-white">{bets.total_count}</b></div>
              <div className="text-[11.5px]"><span className="text-slate-500">Open bets:</span> <b className="text-amber-300">{bets.open_count}</b></div>
            </div>
            {bets.items?.length ? (
              <div className="divide-y divide-white/[0.04] -mx-3">
                {bets.items.map((b, i) => (
                  <div key={b.id || i} className="px-3 py-1.5 text-[11px] flex items-center justify-between gap-2">
                    <div className="truncate">
                      <span className="font-mono text-slate-400">{(b.game_name || b.game || b.market || "Bet").toString().slice(0, 32)}</span>
                      <span className="ml-2 text-slate-600">{b.created_at ? fmtTime(b.created_at) : ""}</span>
                    </div>
                    <div className="shrink-0 font-bold">
                      <span className="text-white">৳{Number(b.stake || b.amount || 0).toLocaleString("en-IN")}</span>
                      <span className={`ml-2 text-[10px] uppercase ${(b.status || "").toLowerCase() === "won" ? "text-emerald-300" : (b.status || "").toLowerCase() === "lost" ? "text-rose-300" : "text-amber-300"}`}>
                        {b.status || "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="text-[11.5px] text-slate-500 py-2 text-center">No bets recorded</div>}
          </>
        ) : (
          <div className="text-[11.5px] text-slate-500">Click &ldquo;Load&rdquo; to fetch recent 20 bets.</div>
        )}
      </div>
    </div>
  );
}

function PermToggleRow({ label, value, onChange, testid, disabled }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[11.5px] text-slate-300">{label}</span>
      <button onClick={() => onChange(!value)} disabled={disabled}
              data-testid={testid}
              className={`relative h-5 w-10 rounded-full border transition-colors disabled:opacity-50 ${
                value ? "bg-emerald-500/40 border-emerald-400/60" : "bg-slate-700/60 border-white/10"
              }`}>
        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${value ? "translate-x-[20px]" : ""}`} />
      </button>
    </div>
  );
}

function ModeChip({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`h-7 px-2.5 rounded-md text-[10.5px] font-bold border transition-colors ${
              active
                ? "bg-violet-500/15 border-violet-400/40 text-violet-200"
                : "bg-white/[0.03] border-white/[0.06] text-slate-400 hover:text-white"
            }`}>
      {label}
    </button>
  );
}

function TxStatusPill({ status }) {
  const s = (status || "").toLowerCase();
  let cls = "bg-slate-500/15 border-slate-400/30 text-slate-300";
  if (["approved", "completed", "success", "paid"].includes(s)) cls = "bg-emerald-500/15 border-emerald-400/30 text-emerald-200";
  else if (["pending", "processing", "initiated", "submitted", "review"].includes(s)) cls = "bg-amber-500/15 border-amber-400/30 text-amber-200";
  else if (["rejected", "failed", "cancelled", "canceled"].includes(s)) cls = "bg-rose-500/15 border-rose-400/30 text-rose-200";
  return (
    <span className={`px-2 h-6 inline-flex items-center rounded-full text-[10px] font-bold border whitespace-nowrap ${cls}`}>
      {status || "—"}
    </span>
  );
}

function TurnoverPane({ t }) {
  if (!t?.available) return <div className="text-[12px] text-slate-500 text-center py-6">Turnover system unavailable</div>;
  return (
    <div className="space-y-3" data-testid="ls-profile-turnover">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Deposit Bucket</div>
        <ProgressBar label="Wagering required" required={t?.deposit?.required} progress={t?.deposit?.progress} />
        <div className="text-[10.5px] mt-2 text-slate-400">
          Remaining: <b className="text-amber-300">৳ {Number(t?.deposit?.remaining || 0).toLocaleString("en-IN")}</b>
        </div>
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Bonus Bucket</div>
        <ProgressBar label="Wagering required" required={t?.bonus?.required} progress={t?.bonus?.progress} locked={t?.bonus?.locked} />
        <div className="text-[10.5px] mt-2 text-slate-400">
          Locked: <b className="text-rose-300">৳ {Number(t?.bonus?.locked || 0).toLocaleString("en-IN")}</b>
          {" · "}Remaining: <b className="text-amber-300">৳ {Number(t?.bonus?.remaining || 0).toLocaleString("en-IN")}</b>
        </div>
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-[12px]">
        <Row k="Withdrawals blocked?" v={t?.block_withdrawal ? "Yes" : "No"} />
        <Row k="Default multiplier" v={`x${t?.default_multiplier ?? "—"}`} />
        <Row k="Bonus multiplier" v={`x${t?.default_bonus_multiplier ?? "—"}`} />
        <Row k="Withdraw allowed" v={t?.completed ? "✅ Yes" : "❌ No (pending)"} highlight />
      </div>
    </div>
  );
}

function ProgressBar({ label, required, progress, locked }) {
  const r = Number(required || 0);
  const p = Math.max(0, Number(progress || 0));
  const pct = r > 0 ? Math.min(100, (p / r) * 100) : (r === 0 ? 100 : 0);
  const done = pct >= 99.999;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono font-bold text-slate-200">
          ৳ {p.toLocaleString("en-IN")} / ৳ {r.toLocaleString("en-IN")}
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`h-full transition-all ${done ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
      </div>
      {locked > 0 && (
        <div className="text-[10px] text-rose-300 mt-1">🔒 Locked: ৳ {Number(locked).toLocaleString("en-IN")}</div>
      )}
    </div>
  );
}

function KycPane({ k, canDecide, onDecide, actionBusy }) {
  const last = k?.last_submission;
  const [acting, setActing] = useState(null); // "approve" | "reject"
  const [note, setNote] = useState("");
  const isPending = (last?.status || "").toLowerCase() === "pending";

  const confirm = async () => {
    if (!acting || !last?.id) return;
    await onDecide?.(last.id, acting, note);
    setActing(null); setNote("");
  };

  return (
    <div className="space-y-3" data-testid="ls-profile-kyc">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">KYC Status</div>
        <KycBadge status={k?.status} />
        <div className="text-[11px] text-slate-400 mt-2">
          Total submissions: <b className="text-white">{k?.submissions_count || 0}</b>
        </div>
      </div>
      {last ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Latest Submission</div>
          <Row k="Submission status" v={last.status || "—"} />
          <Row k="AI verdict" v={last.ai_verdict || "—"} />
          {last.rejection_reason && <Row k="Reject reason" v={last.rejection_reason} />}
          <Row k="Submitted at" v={last.submitted_at ? fmtTime(last.submitted_at) : "—"} />
          <div className="mt-2 flex items-center gap-1.5 text-[11px]">
            <DocBadge label="NID Front" has={last.has_nid_front} />
            <DocBadge label="NID Back" has={last.has_nid_back} />
            <DocBadge label="Selfie" has={last.has_selfie} />
          </div>
          {canDecide && isPending && (
            <div className="mt-3 flex items-center gap-1.5 border-t border-white/[0.06] pt-3">
              <button onClick={() => setActing("approve")} disabled={actionBusy}
                      data-testid="ls-kyc-approve-btn"
                      className="h-8 px-3 rounded-md text-[11px] font-bold text-emerald-200 bg-emerald-500/15 border border-emerald-400/30 hover:bg-emerald-500/25 disabled:opacity-50">
                ✓ Approve KYC
              </button>
              <button onClick={() => setActing("reject")} disabled={actionBusy}
                      data-testid="ls-kyc-reject-btn"
                      className="h-8 px-3 rounded-md text-[11px] font-bold text-rose-200 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 disabled:opacity-50">
                ✕ Reject KYC
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 text-center text-[12px] text-slate-500">
          No KYC submission yet
        </div>
      )}
      {!canDecide && (
        <div className="rounded-lg border border-amber-400/20 bg-amber-500/[0.06] p-2.5 text-[11px] text-amber-200">
          🔒 KYC review permission required. Ask admin to grant <b>kyc.decide</b> to your account.
        </div>
      )}

      {acting && (
        <ConfirmDialog
          title={`${acting === "approve" ? "Approve" : "Reject"} KYC?`}
          subtitle="This will be recorded in the audit log as you (Support)."
          confirmKind={acting === "approve" ? "ok" : "danger"}
          confirmLabel={acting === "approve" ? "Approve" : "Reject"}
          onCancel={() => { setActing(null); setNote(""); }}
          onConfirm={confirm}
          busy={actionBusy}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    placeholder={acting === "reject" ? "Reject reason (shown to user)…" : "Optional note…"}
                    data-testid="ls-kyc-decision-note"
                    className="w-full rounded-lg bg-slate-900/70 border border-white/[0.08] text-[12.5px] text-white placeholder-slate-500 p-2.5 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/20 resize-none" />
        </ConfirmDialog>
      )}
    </div>
  );
}

function KycBadge({ status }) {
  const s = (status || "unverified").toLowerCase();
  let cls = "bg-slate-500/15 border-slate-400/30 text-slate-300";
  let label = status || "Unverified";
  if (s === "verified") { cls = "bg-emerald-500/15 border-emerald-400/30 text-emerald-200"; label = "Verified ✓"; }
  else if (s === "pending") { cls = "bg-amber-500/15 border-amber-400/30 text-amber-200"; label = "Pending Review"; }
  else if (s === "rejected") { cls = "bg-rose-500/15 border-rose-400/30 text-rose-200"; label = "Rejected"; }
  return <span className={`px-2.5 h-7 inline-flex items-center rounded-full text-[11.5px] font-bold border ${cls}`}>{label}</span>;
}

function DocBadge({ label, has }) {
  return (
    <span className={`px-2 h-6 inline-flex items-center gap-1 rounded-full text-[10px] font-bold border ${
      has ? "bg-emerald-500/12 border-emerald-400/30 text-emerald-200" : "bg-white/[0.03] border-white/10 text-slate-500"
    }`}>
      {has ? "✓" : "·"} {label}
    </span>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between py-1 text-[11.5px]">
      <span className="text-slate-500">{k}</span>
      <span className={`font-semibold truncate max-w-[60%] text-right ${highlight ? "text-emerald-300" : "text-slate-200"}`}>{v}</span>
    </div>
  );
}

function KpiPill({ label, value, accent, pulse }) {
  return (
    <div className="h-9 px-3 rounded-lg border border-white/[0.08] bg-white/[0.03] inline-flex items-center gap-2.5">
      <span className="relative flex h-2 w-2">
        {pulse && <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: accent }} />}
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: accent }} />
      </span>
      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">{label}</span>
      <span className="text-[13px] font-black text-white tabular-nums">{value}</span>
    </div>
  );
}

function SessionRow({ s, active, onClick, now }) {
  const hue = hueFromStr(s.username || s.player_id || s.id);
  const isWaiting = s.status === "open";
  const isClaimed = s.status === "claimed";
  const isClosed = s.status === "closed";
  const minutesAgo = (() => {
    if (!s.last_message_at) return 999;
    try { return (Date.now() - new Date(s.last_message_at).getTime()) / 60000; } catch { return 999; }
  })();
  const isStale = isWaiting && minutesAgo > 5;

  // user typing right now?
  const userTyping = (() => {
    if (!s.user_typing_at) return false;
    try { return (now - new Date(s.user_typing_at).getTime()) < 6000; } catch { return false; }
  })();

  return (
    <button onClick={onClick} data-testid={`ls-session-${s.id}`}
            className={`group w-full text-left px-3 py-3 transition-all relative border-l-2 ${
              active
                ? "bg-emerald-500/[0.08] border-emerald-400"
                : "border-transparent hover:bg-white/[0.03]"
            }`}>
      <div className="flex items-start gap-2.5">
        {/* avatar */}
        <div className="relative shrink-0">
          {/* Live-typing pulse rings */}
          {userTyping && (
            <>
              <span aria-hidden className="absolute inset-[-2px] rounded-xl bg-emerald-300/40 pointer-events-none"
                    style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
              <span aria-hidden className="absolute inset-[-2px] rounded-xl bg-emerald-300/25 pointer-events-none"
                    style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) 0.7s infinite" }} />
            </>
          )}
          <div className={`relative h-10 w-10 rounded-xl flex items-center justify-center text-white font-black text-[12px] ring-1 ${userTyping ? "ring-emerald-300/60" : "ring-white/10"}`}
               style={{ background: `linear-gradient(135deg, hsl(${hue},70%,50%), hsl(${(hue + 40) % 360},70%,38%))` }}>
            {initials(s.username || s.player_id)}
          </div>
          {/* status dot */}
          <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[#0e1525] ${
            isClosed ? "bg-slate-500" : isClaimed ? "bg-emerald-400" : "bg-amber-400 animate-pulse"
          }`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="font-bold text-[13px] text-white truncate flex items-center gap-1.5">
              @{s.username || "—"}
              {s.needs_agent && (
                <span title="User needs a real agent (not just FAQ browsing)"
                      data-testid={`ls-needs-agent-${s.id}`}
                      className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wide bg-rose-500/20 text-rose-200 border border-rose-400/40 inline-flex items-center gap-1">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-rose-300 animate-ping opacity-75" />
                    <span className="relative rounded-full h-1.5 w-1.5 bg-rose-300" />
                  </span>
                  Needs Agent
                </span>
              )}
              {!s.needs_agent && isWaiting && (
                <span title="Passive — user is only browsing FAQs"
                      data-testid={`ls-faq-only-${s.id}`}
                      className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-500/15 text-slate-400 border border-slate-500/30">
                  FAQ
                </span>
              )}
              {isStale && s.needs_agent && <span title="Waiting > 5min" className="px-1 rounded text-[9px] font-bold bg-red-500/20 text-red-300 border border-red-500/30">SLA</span>}
            </div>
            <div className="text-[10px] text-slate-500 shrink-0 tabular-nums">{fmtRel(s.last_message_at)}</div>
          </div>

          <div className="text-[10.5px] text-slate-500 mb-1 truncate flex items-center gap-1.5">
            <span className="text-slate-600">ID</span>
            <span className="font-mono font-semibold text-violet-300">{s.player_id || "—"}</span>
            {s.user_phone && <><span className="text-slate-700">·</span><span className="truncate">{s.user_phone}</span></>}
          </div>

          {userTyping ? (
            <div className="text-[12px] text-emerald-300 line-clamp-1 pr-1 leading-snug flex items-center gap-1.5 font-semibold" data-testid={`ls-row-typing-${s.id}`}>
              <span className="inline-flex gap-0.5">
                <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
              typing…
            </div>
          ) : (
            <div className="text-[12px] text-slate-400 line-clamp-1 pr-1 leading-snug">
              {s.last_message || <span className="italic text-slate-600">(no messages yet)</span>}
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-1.5">
            <StatusPill status={s.status} />
            {s.agent_name && (
              <span className="text-[10px] text-slate-500 truncate">
                <span className="text-slate-600">→</span> {s.agent_name}
              </span>
            )}
          </div>
        </div>

        {s.unread_for_agent > 0 && (
          <span className="absolute top-2.5 right-2.5 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center px-1.5 ring-2 ring-[#0e1525] shadow-lg shadow-red-500/30">
            {s.unread_for_agent}
          </span>
        )}
      </div>
    </button>
  );
}

function TypingBubble({ name }) {
  return (
    <div className="flex justify-start gap-2" data-testid="ls-typing-indicator">
      <div className="relative h-8 w-8 shrink-0 self-end">
        <span aria-hidden className="absolute inset-0 rounded-lg bg-emerald-300/40 pointer-events-none"
              style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
        <span aria-hidden className="absolute inset-0 rounded-lg bg-emerald-300/25 pointer-events-none"
              style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) 0.7s infinite" }} />
        <div className="relative h-8 w-8 rounded-lg flex items-center justify-center ring-1 ring-emerald-300/50 bg-white/[0.06] text-emerald-300"
             aria-label="User typing">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-16 0 8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /><path d="M6.5 19a6.5 6.5 0 0 1 11 0" /></svg>
        </div>
      </div>
      <div className="max-w-[40%]">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 text-emerald-300">
          {name} · typing
        </div>
        <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/[0.08] inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function BothTypingBadge() {
  return (
    <span data-testid="ls-both-typing-badge"
          className="inline-flex items-center gap-1 px-2 h-[20px] rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ring-emerald-300/40 text-emerald-200"
          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(5,150,105,0.18) 100%)" }}
          title="Both of you are typing right now">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inset-0 rounded-full bg-emerald-300 opacity-80"
              style={{ animation: "ls-ping-bi 1.2s cubic-bezier(0,0,0.2,1) infinite" }} />
        <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-300" />
      </span>
      Both typing
    </span>
  );
}

function ThreadHeader({ active, agent, userIsTyping, bothTyping, onBack, onClaim, onClose, onToggleProfile, profileOpen }) {
  const hue = hueFromStr(active.username || active.player_id || "u");
  return (
    <div className="px-3 sm:px-5 py-3 border-b border-white/[0.06] flex items-center gap-3 shrink-0"
         style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.06) 0%, transparent 100%)" }}>
      <button onClick={onBack} data-testid="ls-back-to-list"
              className="lg:hidden h-9 w-9 rounded-lg hover:bg-white/5 text-slate-300 flex items-center justify-center shrink-0" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <div className="relative shrink-0">
        {/* Live-typing pulse rings */}
        {userIsTyping && (
          <>
            <span aria-hidden className="absolute inset-[-2px] rounded-xl bg-emerald-300/40 pointer-events-none"
                  style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
            <span aria-hidden className="absolute inset-[-2px] rounded-xl bg-emerald-300/25 pointer-events-none"
                  style={{ animation: "ls-user-pulse 1.4s cubic-bezier(0,0,0.2,1) 0.7s infinite" }} />
          </>
        )}
        <div className={`relative h-11 w-11 rounded-xl flex items-center justify-center text-white font-black text-[13px] ring-1 ${userIsTyping ? "ring-emerald-300/60" : "ring-white/10"}`}
             style={{ background: `linear-gradient(135deg, hsl(${hue},70%,50%), hsl(${(hue + 40) % 360},70%,38%))` }}>
          {initials(active.username || active.player_id)}
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[#0e1525] ${
          active.status === "closed" ? "bg-slate-500" : active.status === "claimed" ? "bg-emerald-400" : "bg-amber-400 animate-pulse"
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-white truncate flex items-center gap-2 text-[15px] leading-tight">
          @{active.username || "—"} <StatusPill status={active.status} />
          {bothTyping && <BothTypingBadge />}
        </div>
        {userIsTyping ? (
          <div className="text-[11px] font-semibold text-emerald-300 mt-0.5 flex items-center gap-1.5" data-testid="ls-header-typing">
            <span className="inline-flex gap-0.5">
              <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="h-1 w-1 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "240ms" }} />
            </span>
            user is typing…
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 truncate flex items-center gap-1.5 mt-0.5">
            <span className="text-slate-600">ID</span>
            <span className="font-mono font-semibold text-violet-300">{active.player_id || "—"}</span>
            {active.user_phone && <><span className="text-slate-700 hidden sm:inline">·</span><span className="hidden sm:inline">📞 {active.user_phone}</span></>}
          </div>
        )}
        {!userIsTyping && active.user_phone && <div className="sm:hidden text-[10.5px] text-slate-400 truncate mt-0.5">📞 {active.user_phone}</div>}
        {active.agent_name && active.agent_id !== agent?.id && (
          <div className="text-[10px] text-amber-300 font-semibold mt-0.5 truncate">Claimed by {active.agent_name}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={onToggleProfile} data-testid="ls-toggle-profile" title="Customer profile"
                className={`h-9 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 border transition-colors ${
                  profileOpen
                    ? "bg-violet-500/15 border-violet-400/40 text-violet-200"
                    : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/10 hover:text-white"
                }`}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span className="hidden sm:inline">Profile</span>
        </button>
        {active.status === "open" && (
          <button onClick={onClaim} data-testid="ls-claim"
                  className="h-9 px-3 rounded-lg text-white text-[12px] font-bold inline-flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 transition-transform active:scale-95"
                  style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            <span className="hidden sm:inline">Claim</span>
          </button>
        )}
        {active.status !== "closed" && (
          <button onClick={onClose} data-testid="ls-close" title="End this chat"
                  className="h-9 px-3 rounded-lg text-red-300 text-[12px] font-bold inline-flex items-center gap-1.5 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-colors">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            <span className="hidden sm:inline">End Chat</span>
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyThread() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
      <div className="relative">
        <div className="h-24 w-24 rounded-3xl flex items-center justify-center ring-1 ring-white/10 mb-5"
             style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.04) 100%)" }}>
          <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="#10b981" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        </div>
        <div className="absolute inset-0 -z-10 blur-2xl opacity-40"
             style={{ background: "radial-gradient(circle, #10b981 0%, transparent 60%)" }} />
      </div>
      <div className="font-black text-white text-[16px]">Select a conversation</div>
      <div className="text-[13px] text-slate-500 mt-1.5 max-w-xs">Pick a session from the inbox on the left to start chatting with the user.</div>
      <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] text-slate-400 max-w-[420px]">
        <Tip icon="⌘" label="Search inbox" />
        <Tip icon="↵" label="Send message" />
        <Tip icon="⇧↵" label="New line" />
      </div>
    </div>
  );
}

function Tip({ icon, label }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-2 flex flex-col items-center gap-1">
      <kbd className="h-6 w-6 rounded-md bg-white/[0.06] border border-white/10 text-white font-bold flex items-center justify-center text-[11px]">{icon}</kbd>
      <span>{label}</span>
    </div>
  );
}

const QUICK_REPLIES_FALLBACK = [
  { category: "সম্ভাষণ", label: "স্বাগতম", text: "আসসালামু আলাইকুম! Maxlive88-এ স্বাগতম 🙏 আমি কীভাবে আপনাকে সাহায্য করতে পারি?" },
  { category: "সম্ভাষণ", label: "অপেক্ষা", text: "একটু অপেক্ষা করুন প্লিজ, আমি আপনার তথ্যটি চেক করে আসছি ⏳" },
  { category: "যাচাই", label: "User ID চাই", text: "অনুগ্রহ করে আপনার Username এবং Player ID শেয়ার করুন।" },
];

function Composer({ disabled, value, onChange, onSend, sending, statusClosed, sessionId, onImageOpen, quickReplies = QUICK_REPLIES_FALLBACK, messages = [] }) {
  const [showQR, setShowQR] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // [{key,file,previewUrl,uploading,url,name,mime,error}]
  const taRef = useRef(null);
  const fileRef = useRef(null);

  // ── Smart suggestions (rule-based, zero LLM cost) ──
  // Re-fetches whenever the LAST user message changes for this session.
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugHidden, setSugHidden] = useState(false);

  // Identify the latest user message timestamp so we refetch only when it changes
  const lastUserMsgKey = (() => {
    if (!Array.isArray(messages) || messages.length === 0) return "";
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i] && messages[i].sender_type === "user") {
        return `${messages[i].id || ""}|${messages[i].created_at || ""}`;
      }
    }
    return "";
  })();

  useEffect(() => {
    if (!sessionId || statusClosed || !lastUserMsgKey) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setSugLoading(true);
    setSugHidden(false);
    axios.get(`${API}/agent/sessions/${sessionId}/suggest-replies?limit=5`, { headers: authHeaders() })
      .then((r) => {
        if (cancelled) return;
        setSuggestions((r.data && r.data.items) || []);
      })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setSugLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, lastUserMsgKey, statusClosed]);

  const insertSuggestion = (s) => {
    onChange(s.text || "");
    // focus the textarea so the agent can review/edit before pressing Enter
    requestAnimationFrame(() => { taRef.current?.focus(); });
  };

  // auto-resize
  useEffect(() => {
    if (!taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = Math.min(taRef.current.scrollHeight, 140) + "px";
  }, [value]);

  const pickFiles = async (list) => {
    if (!sessionId || !list || list.length === 0) return;
    const files = Array.from(list).slice(0, 4);
    const drafts = files.map((f) => ({
      key: `${Date.now()}-${f.name}-${Math.random()}`,
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      uploading: true, url: null, name: f.name, mime: f.type, error: false,
    }));
    setPendingFiles((arr) => [...arr, ...drafts]);
    for (const row of drafts) {
      try {
        const fd = new FormData();
        fd.append("file", row.file);
        const { data } = await axios.post(`${API}/agent/session/${sessionId}/upload`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        setPendingFiles((arr) => arr.map((r) => (r.key === row.key ? { ...r, uploading: false, url: data?.url, name: data?.name || r.name, mime: data?.mime || r.mime } : r)));
      } catch (_) {
        setPendingFiles((arr) => arr.map((r) => (r.key === row.key ? { ...r, uploading: false, error: true } : r)));
      }
    }
  };

  const removePending = (key) => {
    setPendingFiles((arr) => {
      const row = arr.find((r) => r.key === key);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return arr.filter((r) => r.key !== key);
    });
  };

  const submitAll = (e) => {
    e && e.preventDefault();
    const attachments = pendingFiles.filter((p) => p.url).map((p) => ({ name: p.name, url: p.url, mime: p.mime }));
    onSend(attachments);
    // clean previews
    pendingFiles.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setPendingFiles([]);
  };

  const canSend = (!statusClosed && !sending) && (value.trim().length > 0 || pendingFiles.some((p) => p.url));

  return (
    <div className="border-t border-white/[0.06] shrink-0" style={{ background: "#0a0f1c" }}>
      {showQR && !statusClosed && (
        <div className="px-3 sm:px-5 pt-3 pb-1 border-b border-white/[0.05]" data-testid="ls-quick-replies">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Quick Replies — দ্রুত উত্তর</div>
            <button type="button" onClick={() => setShowQR(false)} className="h-6 w-6 rounded-md text-slate-500 hover:text-white hover:bg-white/5 flex items-center justify-center">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="max-h-[260px] overflow-y-auto space-y-2.5 pb-1">
            {Object.entries(
              (quickReplies || []).reduce((acc, q) => {
                const cat = q.cat || q.category || "General";
                (acc[cat] = acc[cat] || []).push(q);
                return acc;
              }, {})
            ).map(([cat, items]) => (
              <div key={cat}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 mb-1.5">{cat}</div>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((q) => (
                    <button key={q.l || q.label} type="button" onClick={() => { onChange(q.t || q.text); setShowQR(false); }}
                            title={q.t || q.text}
                            data-testid={`ls-qr-${q.l || q.label}`}
                            className="h-7 px-2.5 rounded-full bg-white/[0.04] hover:bg-emerald-500/[0.15] hover:border-emerald-400/50 border border-white/10 text-[11px] font-semibold text-slate-200 transition-all">
                      {q.l || q.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {pendingFiles.length > 0 && (
        <div className="px-3 pt-3 flex flex-wrap gap-2" data-testid="ls-agent-pending-files">
          {pendingFiles.map((p) => (
            <div key={p.key} className="relative h-16 w-16 rounded-lg overflow-hidden border border-white/10 bg-white/[0.04] group">
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-[9px] px-1 text-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                  <span className="truncate w-full mt-1">{p.name}</span>
                </div>
              )}
              {p.uploading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                </div>
              )}
              {p.error && <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center text-white text-[10px] font-bold">FAIL</div>}
              <button type="button" onClick={() => removePending(p.key)}
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 border border-white/20 text-white text-[10px] flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submitAll} className="p-2.5 sm:p-3 flex items-end gap-2">
        <button type="button" onClick={() => setShowQR((v) => !v)} disabled={statusClosed} data-testid="ls-toggle-quick-replies"
                title="Quick replies"
                className={`h-11 w-11 rounded-xl border inline-flex items-center justify-center transition-colors shrink-0 ${
                  showQR
                    ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                    : "bg-white/[0.04] border-white/10 text-slate-400 hover:text-white hover:bg-white/[0.08]"
                }`}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" data-testid="ls-agent-file-input"
               onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={statusClosed} data-testid="ls-agent-attach"
                title="Attach image or document"
                className="h-11 w-11 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-400 hover:text-white inline-flex items-center justify-center transition-colors shrink-0">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
        </button>
        <div className="flex-1 relative">
          <textarea ref={taRef} value={value} onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAll(); } }}
                    rows={1}
                    placeholder={statusClosed ? "Session closed — read-only" : "Type a reply…  (Enter to send · Shift+Enter for new line)"}
                    disabled={disabled || sending} data-testid="ls-reply-input"
                    className="w-full px-4 py-3 pr-14 rounded-xl bg-white/[0.04] border border-white/[0.08] text-[14px] focus:outline-none focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/15 resize-none text-white placeholder-slate-500 disabled:opacity-60 transition-all"
                    style={{ minHeight: 44, maxHeight: 140 }} />
          <div className="absolute right-3 bottom-2 text-[10px] text-slate-600 tabular-nums">
            {value.length > 0 && `${value.length}`}
          </div>
        </div>
        <button type="submit" disabled={!canSend} data-testid="ls-reply-send"
                className="h-11 px-4 sm:px-5 rounded-xl font-bold text-[13px] tracking-wide text-white inline-flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition-all active:scale-95 shrink-0"
                style={{ background: !canSend ? "#334155" : "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
          {sending ? (
            <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          ) : (
            <>
              <span className="hidden sm:inline">Send</span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: { c: "bg-amber-500/15 text-amber-300 border-amber-500/30", l: "Waiting", dot: "bg-amber-400" },
    claimed: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", l: "Active", dot: "bg-emerald-400" },
    closed: { c: "bg-slate-500/15 text-slate-400 border-slate-500/30", l: "Closed", dot: "bg-slate-500" },
  };
  const s = map[status] || map.open;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.12em] border ${s.c}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.l}
    </span>
  );
}

function AgentBubble({ m, showHeader, onImageOpen }) {
  if (m.sender_type === "system") {
    return (
      <div className="flex justify-center my-1">
        <div className="max-w-[80%]">
          <div className="rounded-xl px-3 py-1.5 text-[12px] leading-relaxed whitespace-pre-wrap text-slate-400 italic bg-white/[0.04] border border-white/[0.06] text-center">
            {m.text}
          </div>
          {m.options && m.options.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1 justify-center">
              {m.options.map((o, i) => (
                <span key={i} className="px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[10.5px] text-slate-400">↳ {o.label}</span>
              ))}
            </div>
          )}
          <div className="text-[9.5px] text-slate-600 mt-1 text-center">{fmtTime(m.created_at)}</div>
        </div>
      </div>
    );
  }
  const isAgent = m.sender_type === "agent";
  const hasForm = !!m.form_data;
  const hue = hueFromStr(m.sender_name || "u");
  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"} gap-2`}>
      {!isAgent && (
        <div className="h-8 w-8 rounded-lg text-white flex items-center justify-center font-black text-[10px] shrink-0 ring-1 ring-white/10 self-end"
             style={{ background: `linear-gradient(135deg, hsl(${hue},70%,50%), hsl(${(hue + 40) % 360},70%,38%))` }}>
          {initials(m.sender_name)}
        </div>
      )}
      <div className={`max-w-[78%] ${isAgent ? "items-end" : "items-start"} flex flex-col`}>
        {showHeader && (
          <div className={`text-[10px] font-bold uppercase tracking-[0.12em] mb-1 ${isAgent ? "text-emerald-300" : "text-violet-300"}`}>
            {m.sender_name}
          </div>
        )}
        {hasForm ? (
          <FormCard m={m} />
        ) : m.text ? (
          <div className={`px-3.5 py-2 text-[14px] leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
            isAgent
              ? "rounded-2xl rounded-br-sm text-white"
              : "rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/[0.08] text-slate-100"
          }`}
               style={isAgent ? { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" } : undefined}>
            {m.text}
          </div>
        ) : null}
        {!hasForm && m.attachments && m.attachments.length > 0 && (
          <Attachments items={m.attachments} onImageOpen={onImageOpen} />
        )}
        <div className={`text-[9.5px] text-slate-600 mt-1 ${isAgent ? "text-right" : ""}`}>{fmtTime(m.created_at)}</div>
      </div>
    </div>
  );
}

function FormCard({ m }) {
  const fd = m.form_data || {};
  const fields = fd.fields || [];
  const attachments = m.attachments || [];
  return (
    <div className="rounded-xl rounded-bl-sm overflow-hidden ring-1 ring-violet-500/30 shadow-lg"
         style={{ background: "linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.02) 100%)" }}>
      <div className="px-3 py-2 border-b border-violet-500/20 flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
        <div className="text-[12px] font-black text-violet-200">{fd.title || "Form Submission"}</div>
      </div>
      <div className="p-3 text-[13px] space-y-1.5">
        {fields.map((f, i) => (
          <div key={i} className="flex gap-2.5">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 w-28 shrink-0">{f.label}</div>
            <div className="flex-1 min-w-0">
              {f.url ? (
                <a href={f.url.startsWith("http") ? f.url : `${BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer"
                   className="text-emerald-300 underline break-all hover:text-emerald-200">
                  {f.value || "View file"}
                </a>
              ) : (
                <span className="text-slate-100 font-semibold break-all">{f.value || "—"}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {attachments.length > 0 && <div className="px-3 pb-3"><Attachments items={attachments} /></div>}
    </div>
  );
}

function Attachments({ items, onImageOpen }) {
  return (
    <div className="mt-1.5 grid grid-cols-2 gap-1.5">
      {items.map((a, i) => {
        const url = a.url.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`;
        const isImage = (a.mime || "").startsWith("image/");
        return isImage ? (
          <button key={i} type="button" onClick={() => onImageOpen && onImageOpen(url)}
                  className="block rounded-lg overflow-hidden border border-white/10 bg-white/[0.04] hover:opacity-90 transition-opacity">
            <img src={url} alt={a.name} className="w-full h-28 object-cover" />
          </button>
        ) : (
          <a key={i} href={url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
            <div className="p-2 text-[11px] text-slate-300 flex items-center gap-1.5 h-28">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-slate-400"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
              <span className="truncate">{a.name}</span>
            </div>
          </a>
        );
      })}
    </div>
  );
}

/* ============================ View tab + Tickets ============================ */

function ViewTab({ active, onClick, label, badge, badgeColor, pulse, icon, ...rest }) {
  return (
    <button onClick={onClick} {...rest}
            className={`h-9 px-3.5 rounded-xl text-[12px] font-bold inline-flex items-center gap-2 border transition-all ${
              active
                ? "bg-white text-slate-900 border-white shadow-lg shadow-black/30"
                : "bg-white/[0.04] text-slate-300 border-white/10 hover:bg-white/[0.09] hover:border-white/20"
            }`}>
      <span className={active ? "text-emerald-600" : "text-slate-400"}>{icon}</span>
      {label}
      {badge > 0 && (
        <span className="relative inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-black tabular-nums text-white"
              style={{ background: badgeColor }}>
          {pulse && <span className="absolute inset-0 rounded-full animate-ping opacity-50" style={{ background: badgeColor }} />}
          <span className="relative">{badge}</span>
        </span>
      )}
    </button>
  );
}

const TICKET_STATUSES = [
  { k: "pending",     l: "Pending",     color: "#f59e0b", bg: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  { k: "in_progress", l: "In Progress", color: "#3b82f6", bg: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
  { k: "resolved",    l: "Resolved",    color: "#10b981", bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
  { k: "rejected",    l: "Rejected",    color: "#ef4444", bg: "bg-red-500/10 border-red-500/30 text-red-300" },
  { k: "all",         l: "All",         color: "#94a3b8", bg: "bg-white/5 border-white/10 text-slate-300" },
];

function ticketStatusMeta(status) {
  return TICKET_STATUSES.find((s) => s.k === status) || TICKET_STATUSES[0];
}

function TicketsView({ tickets, counts, statusFilter, setStatusFilter, search, setSearch, viewingTicket, setViewingTicket, onUpdated, onOpenSession }) {
  return (
    <div className="px-3 sm:px-5 pt-3 pb-4 flex-1 flex flex-col" data-testid="ls-tickets-view">
      {/* Status chips for tickets */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
        {TICKET_STATUSES.map((s) => {
          const c = s.k === "all"
            ? Object.values(counts || {}).reduce((acc, v) => acc + (v || 0), 0)
            : (counts?.[s.k] || 0);
          const active = statusFilter === s.k;
          return (
            <button key={s.k} onClick={() => setStatusFilter(s.k)} data-testid={`ls-tickets-filter-${s.k}`}
                    className={`h-8 px-3 rounded-full text-[11px] sm:text-xs font-bold inline-flex items-center gap-2 border transition-all ${
                      active
                        ? "bg-white text-slate-900 border-white shadow-lg"
                        : "bg-white/[0.04] text-slate-300 border-white/10 hover:bg-white/[0.08] hover:border-white/20"
                    }`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
              {s.l}
              <span className={`px-1.5 h-4 rounded-full text-[10px] font-bold inline-flex items-center ${active ? "bg-slate-900 text-white" : "bg-white/[0.08] text-slate-400"}`}>{c}</span>
            </button>
          );
        })}
        <div className="ml-auto relative w-full sm:w-72">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          </span>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search by user / phone / TRX ID / amount…"
                 data-testid="ls-tickets-search"
                 className="w-full h-8 pl-8 pr-3 rounded-lg bg-white/[0.04] border border-white/10 text-[12px] text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60" />
        </div>
      </div>

      {/* Grid of ticket cards */}
      <div className="flex-1 overflow-y-auto rounded-2xl ring-1 ring-white/[0.06] p-3 sm:p-4"
           style={{ background: "linear-gradient(180deg, #0e1525 0%, #0a1020 100%)" }}>
        {tickets.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12" data-testid="ls-tickets-empty">
            <div className="h-16 w-16 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
            </div>
            <div className="text-[14px] font-bold text-slate-300">কোনো টিকেট নেই</div>
            <div className="text-[12px] text-slate-500 mt-1">এই ফিল্টারে কোনো ফর্ম সাবমিশন পাওয়া যায়নি</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ls-tickets-grid">
            {tickets.map((t) => (
              <TicketCard key={t.id} t={t} onView={() => setViewingTicket(t.id)} />
            ))}
          </div>
        )}
      </div>

      {viewingTicket && (
        <TicketDrawer
          ticketId={viewingTicket}
          onClose={() => setViewingTicket(null)}
          onUpdated={() => { onUpdated(); }}
          onOpenSession={onOpenSession}
        />
      )}
    </div>
  );
}

function TicketCard({ t, onView }) {
  const sm = ticketStatusMeta(t.status);
  const hue = hueFromStr(t.username || t.player_id || t.id);
  const keyFields = (t.fields || []).filter((f) => !f.url).slice(0, 3);
  const isHot = t.status === "pending";
  const tno = t.ticket_no || "—";
  return (
    <button onClick={onView} data-testid={`ls-ticket-card-${t.id}`}
            className="group relative text-left rounded-2xl p-3.5 border bg-gradient-to-br from-white/[0.04] to-white/[0.02] hover:from-emerald-500/[0.06] hover:to-white/[0.04] hover:border-emerald-400/30 transition-all ring-1 ring-white/[0.04]"
            style={{ borderColor: isHot ? "rgba(245, 158, 11, 0.35)" : "rgba(255,255,255,0.08)" }}>
      {isHot && (
        <span className="absolute -top-1 -right-1 inline-flex items-center px-1.5 h-4 rounded-full text-[9px] font-black text-white bg-amber-500 shadow-lg shadow-amber-500/40">
          <span className="h-1 w-1 rounded-full bg-white mr-1 animate-pulse" /> NEW
        </span>
      )}
      {/* Ticket number — prominent across the top */}
      <div className="flex items-center gap-2 mb-2.5">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.08em] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 font-mono">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
          #{tno}
        </span>
        <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${sm.bg}`}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: sm.color }} />
          {sm.l}
        </span>
      </div>
      <div className="flex items-start gap-2.5">
        <div className="h-9 w-9 shrink-0 rounded-xl flex items-center justify-center text-white text-[11px] font-black ring-1 ring-white/10"
             style={{ background: `linear-gradient(135deg, hsl(${hue} 75% 55%) 0%, hsl(${hue} 75% 38%) 100%)` }}>
          {initials(t.username || t.full_name || "U")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-black text-white truncate">{t.form_title || "Form"}</div>
          <div className="text-[11px] text-slate-400 truncate">
            @{t.username || "guest"} {t.player_id && <span className="font-mono text-purple-300">· #{t.player_id}</span>}
          </div>
        </div>
      </div>
      {keyFields.length > 0 && (
        <div className="mt-2.5 space-y-0.5 border-t border-white/[0.04] pt-2">
          {keyFields.map((f, i) => (
            <div key={i} className="text-[11px] leading-snug flex gap-1.5">
              <span className="text-slate-500 shrink-0">{f.label}:</span>
              <span className="font-mono font-semibold text-slate-200 break-all line-clamp-1">{f.value || "—"}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex items-center justify-between text-[10px] text-slate-500">
        <span>{fmtRel(t.created_at)}</span>
        {(t.attachments || []).length > 0 && (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
            {t.attachments.length}
          </span>
        )}
      </div>
    </button>
  );
}

function TicketDrawer({ ticketId, onClose, onUpdated, onOpenSession }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/agent/tickets/${ticketId}`, { headers: authHeaders() })
      .then(({ data }) => { if (!cancelled) { setTicket(data?.ticket || null); setNote(data?.ticket?.resolution_note || ""); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticketId]);

  const update = async (newStatus) => {
    if (!ticket) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/agent/tickets/${ticketId}`, {
        status: newStatus,
        resolution_note: note || null,
      }, { headers: authHeaders() });
      setTicket(data?.ticket || ticket);
      onUpdated && onUpdated();
    } catch (_) { /* ignore */ }
    finally { setSaving(false); }
  };

  const sm = ticketStatusMeta(ticket?.status || "pending");

  return (
    <div onClick={onClose} data-testid="ls-ticket-drawer"
         className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-end">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-[560px] h-[92dvh] sm:h-full overflow-y-auto rounded-t-2xl sm:rounded-none border-l border-white/10 text-slate-200"
           style={{ background: "linear-gradient(180deg, #0d1325 0%, #080c16 100%)" }}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-4 py-3 flex items-center justify-between border-b border-white/[0.06]"
             style={{ background: "rgba(13,19,37,0.95)", backdropFilter: "blur(12px)" }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300">Ticket</div>
              {ticket?.ticket_no && (
                <button onClick={() => { try { navigator.clipboard?.writeText(ticket.ticket_no); } catch (_) { /* clipboard unavailable */ } }}
                        title="Copy ticket number"
                        data-testid="ls-ticket-no-copy"
                        className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-black font-mono tracking-[0.06em] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors">
                  #{ticket.ticket_no}
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-60 group-hover:opacity-100"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                </button>
              )}
            </div>
            <div className="font-black text-white truncate">{loading ? "Loading…" : ticket?.form_title || "—"}</div>
          </div>
          <button onClick={onClose} data-testid="ls-ticket-close"
                  className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : !ticket ? (
          <div className="p-10 text-center text-slate-500 text-sm">Ticket not found.</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Status + meta */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${sm.bg}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: sm.color }} />
                {sm.l}
              </span>
              <span className="text-[10px] text-slate-500">submitted {fmtRel(ticket.created_at)}</span>
              {ticket.resolved_by && (
                <span className="text-[10px] text-slate-500">· by {ticket.resolved_by}</span>
              )}
            </div>

            {/* User card */}
            <div className="rounded-xl p-3 border border-white/[0.06] bg-white/[0.02]">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5">User</div>
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-xl flex items-center justify-center text-white text-[11px] font-black ring-1 ring-white/10"
                     style={{ background: `linear-gradient(135deg, hsl(${hueFromStr(ticket.username || ticket.player_id)} 75% 55%) 0%, hsl(${hueFromStr(ticket.username || ticket.player_id)} 75% 38%) 100%)` }}>
                  {initials(ticket.username || ticket.full_name || "U")}
                </div>
                <div className="min-w-0">
                  <div className="font-black text-white text-[14px] truncate">@{ticket.username || "—"}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {ticket.player_id && <span className="font-mono text-purple-300">#{ticket.player_id}</span>}
                    {ticket.user_phone && <> · 📞 {ticket.user_phone}</>}
                  </div>
                </div>
              </div>
            </div>

            {/* Fields */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="ls-ticket-fields">
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 border-b border-white/[0.04]">Submitted Fields</div>
              <div className="divide-y divide-white/[0.04]">
                {(ticket.fields || []).map((f, i) => (
                  <div key={i} className="px-3 py-2.5 grid grid-cols-[120px_1fr] gap-3 items-start text-[12.5px]">
                    <div className="text-slate-500 font-semibold">{f.label}</div>
                    <div className="font-mono font-semibold text-white break-all">{f.value || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Attachments */}
            {(ticket.attachments || []).length > 0 && (
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">Attachments ({ticket.attachments.length})</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {ticket.attachments.map((a, i) => {
                    const url = a.url?.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`;
                    const isImage = (a.mime || "").startsWith("image/");
                    return (
                      <a key={i} href={url} target="_blank" rel="noreferrer"
                         className="block rounded-lg overflow-hidden border border-white/10 hover:border-emerald-400/40">
                        {isImage ? (
                          <img src={url} alt={a.name} className="w-full h-20 object-cover" />
                        ) : (
                          <div className="w-full h-20 flex flex-col items-center justify-center text-slate-400 text-[9px] px-1 text-center bg-white/[0.02]">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            <span className="truncate w-full mt-1">{a.name}</span>
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resolution note */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5">Internal / Resolution Note</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                        rows={3} data-testid="ls-ticket-note"
                        placeholder="যেমন: TRX মিলেছে, ক্রেডিট দেওয়া হয়েছে…"
                        className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-[13px] text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60 resize-none" />
            </div>

            {/* Action buttons — hidden when ticket is finalised */}
            {ticket.status === "resolved" || ticket.status === "rejected" ? (
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid="ls-ticket-final-state">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${sm.bg}`}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: sm.color }} />
                    {sm.l}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {ticket.resolved_at ? fmtRel(ticket.resolved_at) : ""}
                  </span>
                </div>
                <div className="text-[12px] text-slate-300 leading-relaxed">
                  {ticket.status === "resolved"
                    ? "এই টিকেটটি সমাধান করা হয়েছে। ব্যবহারকারীকে চ্যাট থ্রেডে এবং ইমেইলে (যদি রেজিস্টার্ড থাকে) জানানো হয়েছে।"
                    : "এই টিকেটটি রিজেক্ট করা হয়েছে। ব্যবহারকারীকে চ্যাট থ্রেডে এবং ইমেইলে (যদি রেজিস্টার্ড থাকে) জানানো হয়েছে।"}
                </div>
                <button onClick={() => update("pending")} disabled={saving} data-testid="ls-ticket-reopen"
                        className="mt-3 w-full h-10 rounded-xl text-[12px] font-black inline-flex items-center justify-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-amber-500/10 hover:border-amber-500/30 hover:text-amber-200 text-slate-300 transition-colors disabled:opacity-50">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                  টিকেট আবার ওপেন করুন (Reopen)
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => update("in_progress")} disabled={saving} data-testid="ls-ticket-action-progress"
                        className="h-10 rounded-xl text-[12px] font-black inline-flex items-center justify-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20 disabled:opacity-50">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                  In Progress
                </button>
                <button onClick={() => update("pending")} disabled={saving} data-testid="ls-ticket-action-pending"
                        className="h-10 rounded-xl text-[12px] font-black inline-flex items-center justify-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 disabled:opacity-50">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  Pending
                </button>
                <button onClick={() => update("resolved")} disabled={saving} data-testid="ls-ticket-action-resolve"
                        className="h-10 rounded-xl text-[12px] font-black inline-flex items-center justify-center gap-2 text-white shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  Resolve
                </button>
                <button onClick={() => update("rejected")} disabled={saving} data-testid="ls-ticket-action-reject"
                        className="h-10 rounded-xl text-[12px] font-black inline-flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 disabled:opacity-50">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  Reject
                </button>
              </div>
            )}

            {/* Open chat session + Download PDF */}
            <div className="grid grid-cols-2 gap-2">
              {ticket.session_id && (
                <button onClick={() => onOpenSession && onOpenSession(ticket.session_id)} data-testid="ls-ticket-open-chat"
                        className="h-10 rounded-xl text-[12px] font-bold inline-flex items-center justify-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-slate-200">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  Open Chat
                </button>
              )}
              <a href={`${API}/agent/tickets/${ticketId}/pdf`}
                 onClick={(e) => {
                   e.preventDefault();
                   const tk = localStorage.getItem("ls_agent_token");
                   fetch(`${API}/agent/tickets/${ticketId}/pdf`, { headers: { Authorization: `Bearer ${tk}` } })
                     .then((r) => r.blob()).then((b) => {
                       const u = URL.createObjectURL(b);
                       const a = document.createElement("a");
                       a.href = u; a.download = `maxlive88-ticket-${ticket.ticket_no || ticketId}.pdf`; a.click();
                       setTimeout(() => URL.revokeObjectURL(u), 1000);
                     });
                 }}
                 data-testid="ls-ticket-download-pdf"
                 className="h-10 rounded-xl text-[12px] font-bold inline-flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-300 cursor-pointer">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Download PDF
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

